import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { EventEmitter, Injectable, Output } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { RetornoSelecao } from '../../../shared/gridview/RetornoSelecao';
import { ServiceCRUD } from '../../../shared/crud/ServiceCRUD';
import { environment } from './../../../../environments/environment';
import { FluxoLeadsResponse, Regional } from './fluxo-leads.model';

@Injectable({
  providedIn: 'root'
})
export class FluxoLeadsService extends ServiceCRUD {
  private baseUrlService: string;
  public carregando = true;
  private httpClient: HttpClient;

  @Output() exibir: EventEmitter<RetornoSelecao> = new EventEmitter<RetornoSelecao>();

  constructor(http: HttpClient, router: Router, protected toastr: ToastrService) {
    super(http, router);
    this.httpClient = http;
    this.baseUrlService = environment.URL_API
  }

  protected getURL(): string {
    return `${this.baseUrlService}/analise_garantia`
  }

  public buscarRegionais(): Promise<Regional[]> {
    const headers = new HttpHeaders({ 'Authorization': this.getToken() });
    
    return this.httpClient.get<any>(`${this.baseUrlService}/filtro/regional`, { headers })
      .toPromise()
      .then(res => {
        if (res && res.filtros) {
          return res.filtros;
        }
        return [];
      })
      .catch(error => {
        this.tratarErro('Não foi possível buscar as regionais.', 'Erro', error);
        return [];
      });
  }

  public buscarDealersRegional(): Promise<any> {
    const headers = new HttpHeaders({ 'Authorization': this.getToken() });
    
    return this.httpClient.get<any>(`${this.baseUrlService}/filtro/dealer_regional`, { headers })
      .toPromise()
      .then(res => {
        if (res && res.filtros) {
          return res.filtros;
        }
        return [];
      })
      .catch(error => {
        this.tratarErro('Não foi possível buscar os dealers por regional.', 'Erro', error);
        return [];
      });
  }

  public buscarFluxoLeadsPorRegional(idRegional: number | null, filtros?: any): Promise<FluxoLeadsResponse> {
    const headers = new HttpHeaders({ 'Authorization': this.getToken() });
    let params = new HttpParams();
    
    if (idRegional !== null && idRegional !== undefined) {
      params = params.set('id_regional', String(idRegional));
    }
    
    // Adiciona filtros se fornecidos
    if (filtros) {
      if (filtros.dealers && filtros.dealers.length > 0) {
        params = params.set('dealers', filtros.dealers.join(','));
      }
      if (filtros.data_inicial) {
        params = params.set('data_inicial', filtros.data_inicial);
      }
      if (filtros.data_final) {
        params = params.set('data_final', filtros.data_final);
      }
    }

    return this.httpClient.get<FluxoLeadsResponse>(`${this.baseUrlService}/fluxo_leads`, { headers, params })
      .toPromise()
      .then(res => res)
      .catch(error => {
        this.tratarErro('Não foi possível buscar os dados de fluxo de leads.', 'Erro', error);
        throw error;
      });
  }

  public buscarClassificacao(data_inicial?: string, data_final?: string, idRegional?: number | null, dealers?: number[]): Promise<any> {
    const headers = new HttpHeaders({ 'Authorization': this.getToken() });
    let params = new HttpParams();
    
    if (data_inicial) {
      params = params.set('data_inicial', data_inicial);
    }
    if (data_final) {
      params = params.set('data_final', data_final);
    }
    
    // Adiciona id_regional se fornecido
    if (idRegional !== null && idRegional !== undefined) {
      params = params.set('id_regional', String(idRegional));
    }
    
    // Adiciona dealers se fornecidos
    if (dealers && dealers.length > 0) {
      params = params.set('dealers', dealers.join(','));
    } else {
      params = params.set('dealers', '');
    }
    
    // Valores vazios para os outros parâmetros obrigatórios
    params = params.set('modelo_veic', '');
    params = params.set('versao_veic', '');
    params = params.set('nome_vendedor', '');
    params = params.set('somente_fabrica', '');

    return this.httpClient.get<any>(`${this.baseUrlService}/funil_vendas_leads/etapas`, { headers, params })
      .toPromise()
      .then(res => res)
      .catch(error => {
        this.tratarErro('Não foi possível buscar os dados de etapas.', 'Erro', error);
        throw error;
      });
  }

  public buscar(filtros: any): Promise<any> {
    const headers = new HttpHeaders({ 'Authorization': this.getToken() });
    let params = new HttpParams();
    if (filtros) {
      Object.keys(filtros).forEach(key => {
        const value = filtros[key];
        if (value !== undefined && value !== null && value !== '') {
          params = params.set(key, String(value));
        }
      });
    }

    return this.httpClient.get(this.getURL(), { headers, params })
      .toPromise()
      .then(this.tratarRetorno);
  }

  tratarErro(mensagem: string, titulo: string, error: any) {
    this.carregando = false;

    console.error(error);

    let texto: string;
    try {
      texto = error.erro ? mensagem + '\n' + error.erro : mensagem;
    } catch (Exception) {
      texto = mensagem;
    }

    this.toastr.error(texto, titulo, error);

    if (error.status === 401) {
      this.router.navigateByUrl('logout');
    }
  }

  public ver(registro: RetornoSelecao) {
    this.carregarModel(registro)
      .then(res => {
        registro = res;
        this.exibir.emit(registro);
      })
      .catch(error => this.tratarErro('Não foi possível carregar o registro.', 'Erro', error));
  }

  protected carregarModel(registro: any): Promise<any> {
    return Promise.resolve(registro);
  }

  private getUser(): string {
    const currentUser = JSON.parse(sessionStorage.getItem('currentUser'));
    return currentUser ? currentUser.username : '';
  }

  public analisar(dadosAnalise: any): Promise<any> {
    const headers = new HttpHeaders({ 
      'Authorization': this.getToken(),
      'Content-Type': 'application/json'
    });

    return this.httpClient.post(`${this.getURL()}/analise`, dadosAnalise, { headers })
      .toPromise()
      .then(this.tratarRetorno)
      .catch(error => {
        this.tratarErro('Não foi possível processar a análise.', 'Erro', error);
        throw error;
      });
  }

  public verificarPermissaoUsuario(): Promise<boolean> {
    const headers = new HttpHeaders({ 
      'Authorization': this.getToken(),
      'Content-Type': 'application/json'
    });

    return this.httpClient.get(`${this.getURL()}/usuario_fabrica`, { headers })
      .toPromise()
      .then((res: any) => {
        return res === true || res === 'true';
      })
      .catch(error => {
        console.error('Erro ao verificar permissão do usuário:', error);
        return false;
      });
  }

}
